/**
 * Secret redaction for observation content.
 *
 * Haiku sometimes captures plaintext API keys / tokens into extracted memory
 * content despite prompt-level instructions to redact. This is a
 * defense-in-depth regex scrub applied to every string before it lands in the
 * `observations.content` column. See docs/memory-architecture.md.
 *
 * Scope: storage only. The extraction LLM call still sees the raw user message
 * containing the key. End-to-end protection would require scrubbing the inbound
 * message before extraction, which is brittle (often destroys context Haiku
 * needs) and we rely on Anthropic retention guarantees for in-flight exposure.
 *
 * The patterns below match common key formats we've seen in real conversations.
 * Anything matched is replaced with `<REDACTED>`. This is conservative — false
 * positives (plain text that looks like a key) are acceptable; a false
 * negative (real key stored plaintext) is not.
 */

/** Patterns for well-known key formats. Order matters only for overlap cases. */
const REDACTION_PATTERNS: readonly { name: string; pattern: RegExp }[] = [
  // Raw location coordinates. Place names are fine; exact lat/lon pairs should
  // not become long-term observations.
  { name: "coordinates", pattern: /\b(?:lat(?:itude)?[:= ]\s*)?-?\d{1,2}\.\d{3,}\s*,\s*(?:lon(?:gitude)?[:= ]\s*)?-?\d{1,3}\.\d{3,}\b/gi },
  // Anthropic OAuth tokens and API keys. sk-ant-oat01-... or sk-ant-api03-...
  { name: "anthropic", pattern: /\bsk-ant-[A-Za-z0-9_-]{20,}/g },
  // PostHog personal API keys (phx_...) and project keys (phc_..., distinct format).
  { name: "posthog", pattern: /\bph[cx]_[A-Za-z0-9_-]{20,}/g },
  // GitHub personal access tokens (both classic and fine-grained).
  { name: "github-pat", pattern: /\bgithub_pat_[A-Za-z0-9_]{20,}/g },
  { name: "github-classic", pattern: /\bghp_[A-Za-z0-9]{20,}/g },
  // Slack bot/user/app tokens.
  { name: "slack", pattern: /\bxox[bpaors]-[A-Za-z0-9-]{10,}/g },
  // Stripe secret / restricted / publishable keys.
  { name: "stripe", pattern: /\b(sk|rk|pk)_(live|test)_[A-Za-z0-9]{20,}/g },
  // Generic OpenAI-style sk- keys. Must come AFTER more-specific sk-ant- pattern.
  // Includes hyphens to catch `sk-proj-…` project keys and similar.
  { name: "openai", pattern: /\bsk-[A-Za-z0-9_-]{20,}/g },
  // Bearer-token auth headers anywhere in prose ("use Bearer abc123..." etc.)
  // Value must be at least 20 chars to avoid matching the word "Bearer" followed by short tokens.
  { name: "bearer", pattern: /\bBearer\s+[A-Za-z0-9_\-.=]{20,}/g },
  // Generic AWS access key id (classic 20-char uppercase prefix).
  { name: "aws-akid", pattern: /\b(AKIA|ASIA)[A-Z0-9]{16}\b/g },
];

/**
 * Redact any known-format secrets in `content`. Returns the redacted string.
 * If no patterns match, the original string is returned unchanged.
 */
export const redact = (content: string): string =>
  REDACTION_PATTERNS.reduce(
    (acc, { pattern }) => acc.replace(pattern, "<REDACTED>"),
    content,
  );

/**
 * For tests / diagnostics: return the names of patterns that matched at least
 * once in `content`, without actually redacting. Useful for verifying which
 * classes of key were seen in a corpus.
 *
 * Recreates each regex per check because `.test()` with the /g flag mutates
 * `lastIndex`; a stateful regex would skip matches across consecutive calls.
 * `redact()` doesn't have this problem — `String.prototype.replace` resets
 * `lastIndex` back to 0, so the cached patterns there are safe.
 */
export const scanSecretKinds = (content: string): string[] =>
  REDACTION_PATTERNS
    .filter(({ pattern }) => new RegExp(pattern.source, pattern.flags).test(content))
    .map(({ name }) => name);
